"use client"

import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { Code2, Database, Layout, Lock, Rocket, Sparkles } from "lucide-react"
import { DiscordProfile } from "./discord-profile"
import { SectionDivider } from "./section-divider"

const skills = [
  {
    icon: Code2,
    title: "JavaScript",
    description: "Experienced with vanilla JS and Node.js development",
    color: "from-yellow-500 to-amber-500",
  },
  {
    icon: Database,
    title: "PostgreSQL",
    description: "Experienced with database design and queries",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Layout,
    title: "React & Next.js",
    description: "Currently learning modern React patterns",
    color: "from-purple-500 to-pink-500",
  },
  {
    icon: Sparkles,
    title: "TypeScript & Tailwind",
    description: "Building type-safe, styled applications",
    color: "from-indigo-500 to-violet-500",
  },
  {
    icon: Rocket,
    title: "Supabase",
    description: "Getting familiar with BaaS and auth",
    color: "from-green-500 to-emerald-500",
  },
  {
    icon: Lock,
    title: "Git & GitHub",
    description: "Version control and collaboration",
    color: "from-gray-500 to-slate-500",
  },
]

export function ModernAbout() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  return (
    <section id="about" className="relative overflow-hidden">
      <SectionDivider variant="wave" />

      <div className="py-32 relative">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-background" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            className="max-w-7xl mx-auto"
          >
            {/* Section header */}
            <div className="text-center mb-20 space-y-4">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-muted/10 border border-border text-foreground backdrop-blur-sm"
              >
                About Me
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-6xl font-bold tracking-tight"
              >
                Skills & Expertise
              </motion.h2>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: 100 }}
                className="h-1 bg-primary rounded-full mx-auto"
              />
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Skills grid */}
              <div className="lg:col-span-2 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="group relative"
                  >
                    <div className="relative h-full p-6 rounded-2xl glass-panel hover:border-primary/50 transition-all duration-500">
                      {/* Gradient background on hover */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${skill.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`} />

                      <div className="relative space-y-4">
                        {/* Icon */}
                        <div className="w-12 h-12 rounded-xl bg-muted/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                          <skill.icon className="w-6 h-6 text-foreground group-hover:text-primary transition-colors duration-500" />
                        </div>

                        {/* Content */}
                        <div className="space-y-2">
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                            {skill.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {skill.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Discord Profile */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="lg:col-span-1"
              >
                <div className="sticky top-24">
                  <DiscordProfile />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <SectionDivider variant="wave" flip />
    </section>
  )
}
